import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ANCHORS } from './anatomy';
import { useSimStore } from '../state/store';

const clamp01 = (v) => Math.max(0, Math.min(1, v));

// Room-scale resolution effects, centered between the two brains.
// Scenario A (Win): golden explosion filling the gallery.
// Scenario B (Loss): jagged crimson shockwave that vanishes quickly.
export default function ResolutionEffects() {
  const winRoom = useRef();
  const winRoomMat = useRef();
  const winCore = useRef();
  const winCoreMat = useRef();
  const lossShell = useRef();
  const lossShellMat = useRef();
  const lossFlashMat = useRef();
  const mileRoom = useRef();
  const mileRoomMat = useRef();
  const mileCore = useRef();
  const mileCoreMat = useRef();

  useFrame(() => {
    const s = useSimStore.getState();
    const et = s.event === 'none' ? 0 : performance.now() / 1000 - s.eventStart;

    const w = s.event === 'win' ? clamp01(et / 2.8) : 0;
    const reward = s.event === 'win' && s.streakType === 'win' && s.streakCount >= 3 ? 1 : 0;
    const wEnv = w > 0 ? Math.sin(w * Math.PI) : 0;
    if (winRoomMat.current) winRoomMat.current.opacity = wEnv * (0.5 + reward * 0.3);
    if (winRoom.current) winRoom.current.scale.setScalar(1 + w * (9 + reward * 6));
    if (winCore.current) winCore.current.scale.setScalar(0.05 + Math.pow(w, 0.5) * (2.6 + reward * 2.2));
    if (winCoreMat.current) winCoreMat.current.opacity = wEnv;

    const l = s.event === 'loss' ? clamp01(et / 1.4) : 0;
    if (lossShell.current) lossShell.current.scale.setScalar(0.1 + l * 4.0);
    if (lossShellMat.current) lossShellMat.current.opacity = l > 0 ? (1 - l) * 0.85 : 0;
    if (lossFlashMat.current) lossFlashMat.current.opacity = l > 0 ? Math.max(0, 1 - l * 5) * 0.5 : 0;

    // Milestone Celebration: grand full-room golden burst on a 5-win streak.
    const now = performance.now() / 1000;
    const m = s.milestone ? clamp01((now - s.milestoneStart) / 4.5) : 0;
    const mEnv = m > 0 ? Math.sin(m * Math.PI) : 0;
    if (mileRoomMat.current) mileRoomMat.current.opacity = mEnv * 0.62;
    if (mileRoom.current) mileRoom.current.scale.setScalar(1 + m * 18);
    if (mileCore.current) {
      mileCore.current.scale.setScalar(0.2 + m * 7);
      mileCore.current.rotation.y = now * 0.6;
      mileCore.current.rotation.x = now * 0.3;
    }
    if (mileCoreMat.current) mileCoreMat.current.opacity = mEnv * 0.9;
  });

  return (
    <group position={ANCHORS.center}>
      <mesh ref={winRoom}>
        <sphereGeometry args={[1, 24, 24]} />
        <meshBasicMaterial ref={winRoomMat} color="#ffd700" transparent opacity={0} side={THREE.BackSide} depthWrite={false} />
      </mesh>
      <mesh ref={winCore}>
        <sphereGeometry args={[0.12, 24, 24]} />
        <meshBasicMaterial ref={winCoreMat} color="#fff2a8" transparent opacity={0} depthWrite={false} />
      </mesh>

      <mesh ref={lossShell}>
        <icosahedronGeometry args={[0.5, 1]} />
        <meshBasicMaterial ref={lossShellMat} color="#ff1a1a" wireframe transparent opacity={0} depthWrite={false} />
      </mesh>
      <mesh>
        <sphereGeometry args={[11, 16, 16]} />
        <meshBasicMaterial ref={lossFlashMat} color="#5a0000" transparent opacity={0} side={THREE.BackSide} depthWrite={false} />
      </mesh>

      {/* Milestone Celebration — room-scale golden bloom + shimmering core */}
      <mesh ref={mileRoom}>
        <sphereGeometry args={[1, 24, 24]} />
        <meshBasicMaterial ref={mileRoomMat} color="#ffcf5a" transparent opacity={0} side={THREE.BackSide} depthWrite={false} />
      </mesh>
      <mesh ref={mileCore}>
        <icosahedronGeometry args={[0.3, 1]} />
        <meshBasicMaterial ref={mileCoreMat} color="#fff2b0" wireframe transparent opacity={0} depthWrite={false} />
      </mesh>
    </group>
  );
}
